import React from 'react';
import { Translate } from 'react-localize-redux';
import OwnershipCtrl from '@webapp/controllers/track/ownership';
import AppState from '@webapp/store/state';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import { ValidOwnershipType } from '@services/ownership/types/valid-ownership-type';
import { SplitUpdate } from '@services/ownership/types/split-update';
import { Ownership } from '@services/ownership/types/ownership';
import {
  CreateOwnership,
  ReassignSplits,
  ReassignSplitsSuccess,
  RemoveOwnership,
  UpdateOwnershipOrder,
} from '@webapp/store/ownership/action-creators';
import arrayMove from 'array-move';
import { NewOwnership } from '@services/ownership/types/new-ownership';
import { assetOwnersByType } from '@webapp/store/ownership/selectors';
import Box from '@components/layout/box';
import isEqual from 'lodash/isEqual';
import cloneDeep from 'lodash/cloneDeep';
import { CollaboratorState } from '@webapp/store/collaborator/state';

type WriterOwnershipProps = {
  releaseId: string;
  trackId: string;
  songName: string;
  locked?: boolean;
  ownerShipAsset: {
    [ownershipId: string]: Ownership;
  };
  ownershipCount: number;
  addWriterToOtherTracks?: boolean;
  assets: CollaboratorState['assets'];

  reassignSplit: (
    trackId: string,
    ownershipType: ValidOwnershipType,
    split: SplitUpdate[],
    newOwnerShip: Ownership[]
  ) => ReassignSplits;
  reassignSplitSuccess: (
    assetId: string,
    ownershipType: ValidOwnershipType,
    splitUpdate: SplitUpdate[],
    newOwnerShips: Ownership[]
  ) => ReassignSplitsSuccess;
  removeOwnership: (assetId: string, ownershipId: string) => RemoveOwnership;
  updateOwnershipOrder: (
    assetId: string,
    order: string[]
  ) => UpdateOwnershipOrder;
  createOwnership: (newOwner: NewOwnership) => CreateOwnership;
};

const mapStateToProps = (state: AppState, { trackId }) => {
  return {
    ownerShipAsset: state.ownership.assets[trackId],
    ownershipCount: Object.keys(state.ownership.assets[trackId] || {}).length,
    assets: state.collaborator.assets,
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  reassignSplit: (
    trackId: string,
    ownershipType: ValidOwnershipType,
    split: SplitUpdate[],
    newOwnerShip: Ownership[]
  ) =>
    dispatch(new ReassignSplits(trackId, ownershipType, split, newOwnerShip)),
  reassignSplitSuccess: (
    assetId: string,
    ownershipType: ValidOwnershipType,
    splitUpdate: SplitUpdate[],
    newOwnerShips: Ownership[]
  ) =>
    dispatch(
      new ReassignSplitsSuccess(
        {
          assetId,
          ownershipType,
          splitUpdate,
          newOwnerShips,
        },
        undefined,
        true
      )
    ),
  createOwnership: (newOwner: NewOwnership) =>
    dispatch(new CreateOwnership(newOwner)),
  removeOwnership: (assetId: string, ownershipId: string) =>
    dispatch(new RemoveOwnership(assetId, ownershipId)),
  updateOwnershipOrder: (assetId: string, order: string[]) =>
    dispatch(new UpdateOwnershipOrder(assetId, order)),
});

const WriterOwnership = ({
  songName,
  trackId,
  releaseId,
  ownerShipAsset,
  locked,
  reassignSplit,
  reassignSplitSuccess,
  removeOwnership,
  createOwnership,
  addWriterToOtherTracks,
  ownershipCount,
  assets,
}: WriterOwnershipProps) => {
  const [ownershipSplitArray, setOwnershipSplitArray] = React.useState<
    Ownership[]
  >([]);
  const [reassignSplitError, SetReassignSplitError] = React.useState(false);

  React.useEffect(() => {
    SetReassignSplitError(false);
  }, [trackId]);

  React.useEffect(() => {
    if (!ownershipCount) {
      return;
    }
    const newOwnershipSplit = assetOwnersByType(ownerShipAsset, 'WRITER');

    if (
      newOwnershipSplit.length > 0 &&
      !isEqual(newOwnershipSplit, ownershipSplitArray)
    ) {
      setOwnershipSplitArray(newOwnershipSplit);
      if (
        newOwnershipSplit.length > 0 &&
        newOwnershipSplit.reduce((memo, owner) => {
          return memo + owner.split * 10 ** 10;
        }, 0) /
          10 ** 10 ===
          100
      ) {
        SetReassignSplitError(false);
      } else {
        SetReassignSplitError(true);
      }
    } else if (newOwnershipSplit.length === 0) {
      SetReassignSplitError(true);
      if (ownershipSplitArray.length) {
        setOwnershipSplitArray(newOwnershipSplit);
      }
    }
  }, [ownerShipAsset, ownershipSplitArray, ownershipCount]);

  const updateWriterSplit = (
    ownershipId: string,
    updatedSplit: number,
    withAutosave: boolean
  ) => {
    const ownerShipWriter = cloneDeep(ownershipSplitArray);

    let newUpdatedArrSplit = ownershipSplitArray.map((ownership) => {
      if (ownership.id === ownershipId) {
        return {
          assetId: trackId,
          ownershipId: ownership.id,
          split: updatedSplit,
        };
      }
      return {
        assetId: trackId,
        ownershipId: ownership.id,
        split: ownership.split,
      };
    });

    let newOwnerShipWriterSplitArr = ownershipSplitArray.filter(
      (splitArr) => !splitArr.id
    );

    if (withAutosave) {
      const ownerShip = ownershipSplitArray.find(
        (ownerShip) => ownerShip.id === ownershipId
      );
      if (!ownerShip) {
        return;
      }
      if (
        newUpdatedArrSplit.length > 0 &&
        newUpdatedArrSplit.reduce((memo, owner) => memo + owner.split, 0) > 100
      ) {
        SetReassignSplitError(true);
        return;
      } else {
        SetReassignSplitError(false);
      }
      reassignSplit(
        trackId,
        'WRITER',
        newUpdatedArrSplit,
        newOwnerShipWriterSplitArr
      );
    } else {
      const newOwnership = ownerShipWriter.map((ownerShip) => {
        if (ownerShip.id === ownershipId) {
          return {
            ...ownerShip,
            split: updatedSplit,
          };
        }
        return ownerShip;
      });

      if (
        newOwnership.length > 0 &&
        newOwnership.reduce((memo, owner) => {
          return memo + owner.split * 10 ** 10;
        }, 0) /
          10 ** 10 ===
          100
      ) {
        SetReassignSplitError(false);
      } else {
        SetReassignSplitError(true);
      }
      setOwnershipSplitArray(newOwnership);
      reassignSplitSuccess(
        trackId,
        'WRITER',
        newUpdatedArrSplit,
        newOwnerShipWriterSplitArr
      );
    }
  };

  const onRemoveWriterOwnership = (ownershipId: string) => {
    removeOwnership(trackId, ownershipId);
  };

  const updateWriterOwnershipOrders = (oldIndex: number, newIndex: number) => {
    const ownerShipWriter = assetOwnersByType(ownerShipAsset, 'WRITER');
    let newOwnership =
      ownershipSplitArray.length > 0 &&
      ownershipSplitArray.filter((writer) => writer.assetId === trackId)
        .length > 0
        ? ownershipSplitArray
        : ownerShipWriter;
    let newOwners = arrayMove(newOwnership, oldIndex, newIndex);
    newOwners = newOwners.map((owner, index) => {
      owner.order = index;
      return owner;
    });
    if (
      newOwners.length > 0 &&
      newOwners.reduce((memo, owner) => {
        return memo + owner.split * 10 ** 10;
      }, 0) /
        10 ** 10 ===
        100
    ) {
      SetReassignSplitError(false);
    }
    setOwnershipSplitArray(newOwners);
  };

  const createWriterOwnerships = (newOwnerShip: Ownership) => {
    const ownerShipWriter = cloneDeep(ownershipSplitArray);
    let newOwnershipArr =
      ownershipSplitArray.length > 0 &&
      ownershipSplitArray.filter((writer) => writer.assetId === trackId)
        .length > 0
        ? ownershipSplitArray
        : ownerShipWriter;

    if (addWriterToOtherTracks) {
      Object.keys(assets).forEach((key) => {
        if (key !== trackId) {
          createOwnership({
            ...newOwnerShip,
            assetId: key,
          });
        }
      });
    }
    createOwnership(newOwnerShip);

    newOwnershipArr.push(newOwnerShip);
    if (
      newOwnershipArr.length > 0 &&
      newOwnershipArr.reduce((memo, owner) => {
        return memo + owner.split * 10 ** 10;
      }, 0) /
        10 ** 10 ===
        100
    ) {
      SetReassignSplitError(false);
    }
    setOwnershipSplitArray(newOwnershipArr);
  };

  return (
    <Box noBorderTop noBorderBottom>
      <OwnershipCtrl
        id="songwriterDropdown"
        title={<Translate id="RELEASE.TRACK_INFO.TRACK.SONGWRITERS.TITLE" />}
        text={<Translate id="RELEASE.TRACK_INFO.TRACK.SONGWRITERS.TEXT" />}
        songName={songName}
        trackId={trackId}
        releaseId={releaseId}
        type="WRITER"
        reassignSplitError={reassignSplitError}
        //needs to be integrated
        updateSplitRequest={() => null}
        ownershipData={ownershipSplitArray}
        updateSplit={updateWriterSplit}
        removeSplit={onRemoveWriterOwnership}
        // integrate ownershipOrder with autosave
        updateOrder={updateWriterOwnershipOrders}
        createOwnershipOnSubmit={createWriterOwnerships}
        withoutGiveMessage
        setGiveawayError={(_value) => null}
        locked={locked}
      />
    </Box>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(WriterOwnership);
